import { type FC, useRef } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { FaPlus } from 'react-icons/fa'

import { Button } from '@/components/elements'
import { ACTIONS } from '@/constants/common/message.constants'

import { ShipmentOrderWaypointRow } from './ShipmentOrderWaypointRow'

type Props = {
  tenantKey: string
  /** 荷主ID（各行の地点絞り込み用） */
  customerId?: number | null
  /**
   * フォーム内のフィールドパス接頭辞（末尾ドット込み、例 `shipments.0.`）。
   * ルート直下の `waypoints` を使う場合は省略（既定 `''`）。配車情報の編集モーダルのように
   * `shipments.${index}.waypoints` へネストする呼び出し側で指定する。
   */
  namePrefix?: string
  /** 各行に到着予定時刻（#5548）の入力欄を出す。配車板のモーダルだけ true */
  showEntryTime?: boolean
  /** 到着予定時刻のカレンダーで選べる日の範囲（矢羽根の時間帯）。未指定なら制限しない */
  entryTimeRange?: {
    startAt: Date | string | null | undefined
    endAt: Date | string | null | undefined
  }
}

/**
 * 配送依頼の経由地（発地→着地間、0〜可変個）セクション。
 *
 * `useFieldArray('waypoints')` で行を増減する。経由地は 0 件可のため空表示は出さず、
 * 追加ボタンのみ常時表示する。各行の局所エリア状態は行コンポーネント側が保持する。
 */
export const ShipmentOrderWaypointsSection: FC<Props> = ({
  tenantKey,
  customerId,
  namePrefix = '',
  showEntryTime = false,
  entryTimeRange
}) => {
  const { control } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${namePrefix}waypoints`,
    keyName: 'rhfId'
  })
  // 到着予定時刻の検証（各行の validateEntryTimes）は全行をまとめて見直すので、古い非同期
  // 検証を捨てる連番はセクションで 1 本持って全行に渡す（行ごとだと行またぎで古い結果が残る）
  const entryTimeValidationSeqRef = useRef(0)

  return (
    <div>
      {fields.map((field, index) => (
        <ShipmentOrderWaypointRow
          key={field.rhfId}
          index={index}
          tenantKey={tenantKey}
          customerId={customerId}
          namePrefix={namePrefix}
          showEntryTime={showEntryTime}
          entryTimeRange={entryTimeRange}
          entryTimeValidationSeqRef={entryTimeValidationSeqRef}
          onRemove={remove}
        />
      ))}
      <Button
        type="link"
        icon={<FaPlus />}
        // 作業内容は経由地の既定値（その他）で始める。到着予定時刻は未入力
        onClick={() =>
          append({
            siteId: null,
            note: null,
            workType: 'OTHER',
            entryTime: null
          })
        }
        className="p-0"
      >
        {ACTIONS.ADD_WAYPOINT}
      </Button>
    </div>
  )
}
